import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { CustomerTotals, PaymentView } from '../api/types'
import {
  Amt,
  Card,
  ConfirmDelete,
  DateChip,
  Dt,
  Empty,
  Note,
  NumberCell,
  NumberInput,
  PageHead,
  TotalBox,
} from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'
import { useCustomerPicker } from '../hooks/usePickers'
import { formatMoney, today } from '../lib/format'

interface Receipt {
  balanceBefore: string
  balanceAfter: string
}

/**
 * Money received.
 *
 * For the owner the balance is shown before the entry and again after it. The
 * person handing the money over is standing at the counter, and being able to
 * read both figures out loud settles an argument before it starts.
 *
 * The counter account writes the same receipt and sees neither figure - the
 * server does not send them. It takes the money and records it; what that leaves
 * outstanding is not its business.
 *
 * Underneath sits the same correctable list that the sale page has. A wrong
 * figure here is the one people come back about, and until this list existed the
 * only remedy was to delete the whole entry and type it again.
 */
export function Payments() {
  const customers = useCustomerPicker()
  const [customerId, setCustomerId] = useState<string | null>(null)
  /*
   * The three figures at the top, and the before/after on the receipt, are a
   * customer's balance by another name. The counter takes the money and writes
   * it down; what that leaves outstanding is the owner's to read. So the totals
   * are not fetched at all for a STAFF account - the route would refuse them,
   * and asking would only produce an error where a quiet omission is correct.
   */
  const { isOwner } = useAuth()
  const totals = useApi<CustomerTotals>(
    isOwner && customerId ? `/customers/${customerId}/totals` : null,
  )
  const recent = useApi<PaymentView[]>('/payments/recent?limit=12')

  const [date, setDate] = useState(today())
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [receipt, setReceipt] = useState<Receipt | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function refresh() {
    recent.reload()
    totals.reload()
    customers.reload()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    setReceipt(null)
    setBusy(true)
    try {
      const response = await api.post<Receipt>('/payments', { date, customerId, amount, note })
      setReceipt(response.data)
      // The owner reads the answer off the receipt card below. The counter has no
      // receipt card - it carries balances - so it needs to be told plainly that
      // the money went in, or there is no sign at all that anything happened.
      if (!isOwner) setDone(`وصولی محفوظ — ${formatMoney(amount)}`)
      setAmount('')
      setNote('')
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  /** Corrects the figure in place, keeping the day it was taken and its note. */
  async function correct(payment: PaymentView, newAmount: string) {
    setError(null)
    setDone(null)
    try {
      await api.put(`/payments/${payment.id}`, {
        date: payment.date,
        amount: newAmount,
        note: payment.note ?? null,
      })
      setDone('وصولی ٹھیک کر دی گئی')
      // The receipt above was worked out from the old figure, so it is no longer
      // true and must not be left sitting on the screen.
      setReceipt(null)
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function remove(id: string) {
    setError(null)
    setDone(null)
    try {
      await api.delete(`/payments/${id}`)
      setDone('وصولی ڈیلیٹ ہو گئی')
      setReceipt(null)
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const rows = recent.data ?? []
  const chosen = customers.people.find((person) => person.id === customerId)

  /*
   * What he owes, and what he will owe.
   *
   * <p>Both are null for a counter account and that is the whole point: the
   * server does not send a STAFF caller a balance, so there is nothing here to
   * show, no quick-fill to offer and no "after" line. The screen still takes
   * the money and writes it down, which is the counter's job.
   */
  const owes = isOwner && totals.data ? Number(totals.data.balance) : null
  const paying = Number(amount)
  const after = owes !== null && amount !== '' && Number.isFinite(paying) ? owes - paying : null

  return (
    <>
      <PageHead
        title="ادائیگی"
        hint="گاہک سے پیسے وصول ہوئے۔"
        aside={<DateChip value={date} onChange={setDate} />}
      />

      <Card>
        {error && <Note kind="error">{error}</Note>}
        {done && <Note kind="info">{done}</Note>}

        <form onSubmit={submit}>
          {/*
            Who, first - because that is the order it happens in. A man is
            standing at the counter and the question is how much he is handing
            over, which cannot be judged without knowing what he owes.
          */}
          <div className="buyer">
            {chosen ? (
              <span className="buyer__who">
                <b>{chosen.name}</b>
                {owes !== null && (
                  <span className={`buyer__due${owes > 0 ? ' is-due' : ''}`}>
                    بقایا <span className="amt">{formatMoney(owes)}</span>
                  </span>
                )}
              </span>
            ) : (
              <span className="buyer__who muted">گاہک چنیں</span>
            )}

            <PersonPicker
              label=""
              triggerLabel={chosen ? 'بدلیں' : undefined}
              value={customerId}
              onChange={(id) => {
                setCustomerId(id)
                setReceipt(null)
              }}
              people={customers.people}
              recentIds={customers.recentIds}
              loading={customers.loading}
            />
          </div>

          {/* The one number anybody types here, so the biggest thing on screen. */}
          <label className="big-field">
            <span className="field-head">کتنے پیسے ملے؟</span>
            <NumberInput value={amount} onChange={setAmount} placeholder="0" />
          </label>

          {/*
            Settling the lot is the commonest payment there is, and nobody
            should have to key 228,210 by hand to make it. Only for the owner,
            because only the owner is sent a balance to offer.
          */}
          {owes !== null && owes > 0 && (
            <div className="quickfill">
              <button type="button" onClick={() => setAmount(String(owes))}>
                پورا بقایا <b>{formatMoney(owes)}</b>
              </button>
              <button type="button" onClick={() => setAmount(String(Math.round(owes / 2)))}>
                آدھا <b>{formatMoney(Math.round(owes / 2))}</b>
              </button>
            </div>
          )}

          {/*
            What he will owe once this is written, before it is written. It is
            the figure about to be said out loud across the counter, so it
            belongs on screen while there is still time to check the amount.
          */}
          {after !== null && (
            <div className="after-line">
              <span>{after < 0 ? 'اُس کے پاس جمع ہو جائے گا' : 'اس کے بعد بقایا رہے گا'}</span>
              <b>{formatMoney(Math.abs(after))}</b>
            </div>
          )}

          {/* Rarely filled, so it asks rather than occupying a line of its own. */}
          <details className="more-field">
            <summary>تفصیل لکھنی ہے؟</summary>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              placeholder="مثلاً: آدھے پیسے، باقی کل"
            />
          </details>

          <button className="btn btn--save" disabled={!customerId || !amount || busy}>
            {busy ? 'لکھ رہے ہیں…' : 'وصولی محفوظ کریں'}
          </button>
        </form>
      </Card>

      {isOwner && totals.data && (
        <div className="totals">
          <TotalBox label="کل فروخت" value={<Amt value={totals.data.totalSale} />} />
          <TotalBox label="کل ادائیگی" value={<Amt value={totals.data.totalPaid} />} />
        </div>
      )}

      {isOwner && receipt && (
        <Card title="وصولی ہو گئی">
          <div className="totals">
            <TotalBox label="پہلے بقایا تھا" value={<Amt value={receipt.balanceBefore} />} />
            <TotalBox
              label="اب بقایا ہے"
              value={<Amt value={receipt.balanceAfter} tone="balance" />}
              accent
            />
          </div>
        </Card>
      )}

      <Card title="پچھلی وصولیاں">
        <p className="muted small" style={{ marginTop: 0 }}>
          غلطی سے لکھی گئی رقم یہیں ٹھیک کریں یا ڈیلیٹ کر دیں۔
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="col-extra">تاریخ</th>
                <th>گاہک</th>
                <th className="col-extra">تفصیل</th>
                <th>رقم</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((payment) => (
                <tr key={payment.id} className="row-payment">
                  <td className="col-extra">
                    <Dt value={payment.date} />
                  </td>
                  <td className="name-cell">{payment.customerName}</td>
                  <td className="col-extra">{payment.note || '—'}</td>
                  <td className="num">
                    <NumberCell
                      ariaLabel="رقم"
                      value={payment.amount}
                      width={110}
                      onCommit={(value) => void correct(payment, value)}
                    />
                  </td>
                  <td>
                    <ConfirmDelete onConfirm={() => void remove(payment.id)} />
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <Empty>ابھی کوئی وصولی نہیں لکھی گئی۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <p className="muted small" style={{ marginTop: 10 }}>
            آخری <span className="amt">{rows.length}</span> وصولیاں دکھائی جا رہی ہیں۔ پورا حساب
            «کھاتہ» میں دیکھیں۔ ڈیلیٹ کی ہوئی وصولی «مزید» میں سے 30 دن تک واپس آ سکتی ہے۔
          </p>
        )}
      </Card>
    </>
  )
}
